import { useState } from 'react'
import { motion } from 'framer-motion'
import { Menu, X, Sun, Moon, User, LogOut, Key, Search, MessageSquare, Users, Settings, Info, Compass, Plus, Bookmark, FileText, Zap, Bell } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useAdmin } from '../../contexts/AdminContext'
import { useTheme } from '../../contexts/ThemeContext'
import { Sidebar } from './Sidebar'
import { useNostrProfile } from '../../hooks/useNostrProfile'
import { getAvatarUrl } from '../../lib/avatarUtils'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useBookmarks } from '../../hooks/useBookmarks'
import { UserAvatar } from '../Common/UserAvatar'
import { NotificationDropdown } from '../Notifications/NotificationDropdown'
import { useNotifications } from '../../hooks/useNotifications'

export function Layout({ children }: { children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const { pubkey, logout, isAdmin } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const { bookmarks } = useBookmarks()
  const { settings } = useAdmin()
  const isAdminUser = isAdmin
  const { unreadCount } = useNotifications(pubkey)

  const isInbox = location.pathname.startsWith('/inbox')
  const isAdminPage = location.pathname.startsWith('/admin')
  const hideSidebars = isInbox || isAdminPage

  const { metadata } = useNostrProfile(pubkey)

  const displayName = metadata?.display_name || metadata?.name || (pubkey ? `${pubkey.slice(0, 8)}...` : '')
  const avatarUrl = metadata?.picture || null

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchTerm.trim()) {
      const title = searchTerm.trim()
      const slug = title.toLowerCase().replace(/\s+/g, '-')
      navigate(`/topic/${slug}--${encodeURIComponent(title)}`)
      setSearchTerm('')
    }
  }


  const isImagePage = location.pathname.startsWith('/img/')
  if (isImagePage) return <>{children}</>

  return (
    <div className="min-h-screen bg-surface-50 dark:bg-surface-950">
      {/* Desktop Header */}
      <header className="hidden lg:block bg-white dark:bg-surface-900 border-b border-surface-200 dark:border-surface-700 sticky top-0 z-40">
        <div className="max-w-[1920px] mx-auto px-6 py-4 flex items-center justify-between gap-6">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/img/icon.png"
              alt="Mavililer Sözlük"
              className="w-8 h-8"
            />
            <span className="font-poppins font-semibold text-xl text-gray-900 dark:text-white">
              mavililer sözlük
            </span>
          </Link>

          {/* Search & Actions Bar */}
          <div className="flex-1 max-w-3xl flex items-center gap-4">
            {pubkey && (settings.features.flowEnabled || isAdminUser) && (
              <Link
                to="/flow/mavililer"
                className="p-2.5 bg-surface-50 dark:bg-surface-800 hover:bg-mavi-50 dark:hover:bg-mavi-900/30 text-gray-500 dark:text-gray-400 hover:text-mavi-500 rounded-xl transition-all border border-surface-200 dark:border-surface-700 hover:border-mavi-500/50 group"
                title="Akış"
              >
                <Compass className="w-5 h-5 group-hover:scale-110 transition-transform" />
              </Link>
            )}

            <form onSubmit={handleSearch} className="flex-1 relative group">
              <div className="flex items-center bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-xl focus-within:ring-2 focus-within:ring-mavi-500/20 focus-within:border-mavi-500 transition-all overflow-hidden">
                <Search className="ml-4 text-gray-400 group-focus-within:text-mavi-500 w-5 h-5 transition-colors" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Başlık veya #arama..."
                  className="flex-1 pl-3 pr-4 py-3 bg-transparent focus:outline-none text-gray-900 dark:text-white"
                />

                {pubkey && (
                  <Link
                    to="/create-topic"
                    className="mr-1 my-1 px-4 py-2 bg-gradient-to-r from-mavi-500 to-mavi-600 hover:from-mavi-600 hover:to-mavi-700 text-white rounded-lg font-poppins font-bold transition-all flex items-center gap-2 text-xs shadow-md shadow-mavi-500/20 active:scale-95"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>YENİ BAŞLIK</span>
                  </Link>
                )}
              </div>
            </form>
          </div>

          {/* Right Side - Profile & Theme */}
          <div className="flex items-center gap-3">
            {pubkey && (
              <NotificationDropdown />
            )}

            <button
              onClick={toggleTheme}
              className="p-2 rounded-xl hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors text-gray-600 dark:text-gray-400"
              title={theme === 'light' ? 'Karanlık Mod' : 'Aydınlık Mod'}
            >
              {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </button>

            {pubkey ? (
              <div className="relative group">
                <button className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors">
                  <img
                    src={avatarUrl || getAvatarUrl(displayName)}
                    alt={displayName}
                    className="w-8 h-8 rounded-full object-cover ring-2 ring-surface-200 dark:ring-surface-700"
                  />
                  <span className="font-poppins font-medium text-sm text-gray-900 dark:text-white">
                    {displayName}
                  </span>
                </button>

                {/* Dropdown Menu */}
                <div className="absolute right-0 top-full mt-2 w-64 bg-white dark:bg-surface-900 rounded-2xl border border-surface-200 dark:border-surface-700 shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="p-4 border-b border-surface-200 dark:border-surface-700">
                    <div className="flex items-center gap-3">
                      <img
                        src={avatarUrl || getAvatarUrl(displayName)}
                        alt={displayName}
                        className="w-12 h-12 rounded-full object-cover ring-2 ring-surface-200 dark:ring-surface-700"
                      />
                      <div>
                        <p className="font-poppins font-semibold text-gray-900 dark:text-white">
                          {displayName}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-2">
                    <Link
                      to={`/profile/${pubkey}`}
                      className="w-full flex items-center gap-3 p-3 text-sm hover:bg-surface-100 dark:hover:bg-surface-800 rounded-xl transition-colors text-gray-700 dark:text-gray-300"
                    >
                      <User className="w-4 h-4 text-gray-400" />
                      Profilim
                    </Link>
                    <Link
                      to="/notifications"
                      className="w-full flex items-center gap-3 p-3 text-sm hover:bg-surface-100 dark:hover:bg-surface-800 rounded-xl transition-colors text-gray-700 dark:text-gray-300"
                    >
                      <Bell className="w-4 h-4 text-gray-400" />
                      Bildirimler
                    </Link>
                    <Link
                      to="/inbox"
                      className="w-full flex items-center gap-3 p-3 text-sm hover:bg-surface-100 dark:hover:bg-surface-800 rounded-xl transition-colors text-gray-700 dark:text-gray-300"
                    >
                      <MessageSquare className="w-4 h-4 text-gray-400" />
                      Mesajlar
                    </Link>
                    <Link
                      to="/settings"
                      className="w-full flex items-center gap-3 p-3 text-sm hover:bg-surface-100 dark:hover:bg-surface-800 rounded-xl transition-colors text-gray-700 dark:text-gray-300"
                    >
                      <Settings className="w-4 h-4 text-gray-400" />
                      Ayarlar
                    </Link>
                    {isAdmin && (
                      <Link
                        to="/admin"
                        className="w-full flex items-center gap-3 p-3 text-sm hover:bg-surface-100 dark:hover:bg-surface-800 rounded-xl transition-colors text-gray-700 dark:text-gray-300"
                      >
                        <Settings className="w-4 h-4 text-gray-400" />
                        Admin Paneli
                      </Link>
                    )}
                    <button
                      onClick={logout}
                      className="w-full flex items-center gap-3 p-3 text-sm hover:bg-kirmizi-50 dark:hover:bg-kirmizi-900/20 text-kirmizi-600 dark:text-kirmizi-400 rounded-xl transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      Çıkış Yap
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <Link
                to="/login"
                className="px-6 py-2 rounded-xl bg-mavi-500 text-white hover:bg-mavi-600 transition-colors font-poppins font-medium text-sm"
              >
                Giriş Yap
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Header */}
      <header className="lg:hidden bg-white dark:bg-surface-900 border-b border-surface-200 dark:border-surface-700 sticky top-0 z-40">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
            >
              <Menu className="w-5 h-5" />
            </button>

            <img
              src="/img/icon.png"
              alt="Mavililer Sözlük"
              className="w-8 h-8"
            />
          </div>

          <div className="flex-1 max-w-lg mx-4">
            <form onSubmit={handleSearch} className="relative group">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-mavi-500 w-4 h-4 transition-colors" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Başlık ara..."
                className="w-full pl-10 pr-4 py-2 rounded-xl border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800 focus:bg-white dark:focus:bg-surface-900 focus:outline-none focus:ring-2 focus:ring-mavi-500/20 focus:border-mavi-500 transition-all text-sm"
              />
            </form>
          </div>

          <div className="flex items-center gap-2">

            {pubkey ? (
              <div className="relative group">
                <button className="flex items-center justify-center hover:opacity-80 transition-opacity overflow-hidden">
                  <UserAvatar
                    src={avatarUrl}
                    name={displayName}
                    size="sm"
                    isExternal={false}
                  />
                </button>

                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-surface-900 rounded-lg shadow-lg border border-surface-200 dark:border-surface-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="p-3 border-b border-surface-200 dark:border-surface-700">
                    <p className="font-poppins font-medium text-xs break-all">{displayName}</p>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400">
                      Sözlük Hesabı
                    </p>
                  </div>
                  <Link
                    to={`/profile/${pubkey}`}
                    className="w-full flex items-center gap-2 p-3 text-sm hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
                  >
                    <User className="w-4 h-4 text-gray-400" />
                    Profilim
                  </Link>
                  <Link
                    to="/notifications"
                    className="w-full flex items-center gap-2 p-3 text-sm hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
                  >
                    <Bell className="w-4 h-4 text-gray-400" />
                    Bildirimler
                    {unreadCount > 0 && (
                      <span className="ml-auto px-1.5 py-0.5 text-[10px] bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full font-bold">
                        {unreadCount}
                      </span>
                    )}
                  </Link>
                  {(settings.features.bookmarksEnabled || isAdminUser) && (
                    <Link
                      to="/bookmarks"
                      className="w-full flex items-center gap-2 p-3 text-sm hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
                    >
                      <Bookmark className="w-4 h-4 text-gray-400" />
                      Kaydedilenler
                      {bookmarks.length > 0 && (
                        <span className="ml-auto px-1.5 py-0.5 text-[10px] bg-mavi-100 dark:bg-mavi-900/30 text-mavi-600 dark:text-mavi-400 rounded-full">
                          {bookmarks.length}
                        </span>
                      )}
                    </Link>
                  )}
                  <Link
                    to="/inbox"
                    className="w-full flex items-center gap-2 p-3 text-sm hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
                  >
                    <MessageSquare className="w-4 h-4 text-gray-400" />
                    Mesajlar
                  </Link>
                  {(settings.features.chatEnabled || isAdminUser) && (
                    <Link
                      to="/rooms"
                      className="w-full flex items-center gap-2 p-3 text-sm hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
                    >
                      <Users className="w-4 h-4 text-gray-400" />
                      Odalar
                    </Link>
                  )}
                  {(settings.features.flowEnabled || isAdminUser) && (
                    <Link
                      to="/flow/mavililer"
                      className="w-full flex items-center gap-2 p-3 text-sm hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
                    >
                      <Compass className="w-4 h-4 text-gray-400" />
                      Akış
                    </Link>
                  )}
                  {(settings.features.searchEnabled || isAdminUser) && (
                    <Link
                      to="/search"
                      className="w-full flex items-center gap-2 p-3 text-sm hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
                    >
                      <Search className="w-4 h-4 text-gray-400" />
                      Gelişmiş Arama
                    </Link>
                  )}
                  <Link
                    to="/settings"
                    className="w-full flex items-center gap-2 p-3 text-sm hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
                  >
                    <Settings className="w-4 h-4 text-gray-400" />
                    Ayarlar
                  </Link>
                  <button
                    onClick={logout}
                    className="w-full flex items-center gap-2 p-3 text-sm hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors rounded-b-lg"
                  >
                    <LogOut className="w-4 h-4" />
                    Çıkış Yap
                  </button>
                </div>
              </div>
            ) : (
              <Link
                to="/login"
                className="p-2 rounded-lg bg-mavi-500 text-white hover:bg-mavi-600 transition-colors"
                title="Giriş Yap"
              >
                <Key className="w-5 h-5" />
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <motion.aside
        initial={{ x: '-100%' }}
        animate={{ x: sidebarOpen ? 0 : '-100%' }}
        exit={{ x: '-100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="lg:hidden fixed top-0 left-0 h-full w-72 bg-white dark:bg-surface-900 border-r border-surface-200 dark:border-surface-700 z-50"
      >
        <div className="flex items-center justify-between p-4 border-b border-surface-200 dark:border-surface-700">
          <h2 className="font-poppins font-semibold text-lg">Gündem</h2>
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="h-full overflow-y-auto pb-20">
          <Sidebar />
        </div>
      </motion.aside>

      <div className="flex">
        {/* Desktop Sidebar */}
        {!hideSidebars && (
          <aside className="hidden lg:block w-72 bg-white dark:bg-surface-900 border-r border-surface-200 dark:border-surface-700 min-h-screen sticky top-0">
            <Sidebar />
          </aside>
        )}

        {/* Main Content */}
        <main className={`flex-1 w-full ${isInbox ? 'h-screen overflow-hidden' : isAdminPage ? 'max-w-7xl mx-auto px-4 py-6 lg:py-8' : 'max-w-4xl mx-auto px-4 py-6 lg:py-8'}`}>
          {children}
        </main>

        {/* Right Sidebar (Desktop) */}
        {!hideSidebars && (
          <aside className="hidden xl:block w-72 bg-white dark:bg-surface-900 border-l border-surface-200 dark:border-surface-700 min-h-screen sticky top-0">
            <div className="p-4 space-y-6">
              {/* Hızlı Erişim */}
              {pubkey && (
                <div className="space-y-3">
                  <h3 className="text-xs font-poppins font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                    Hızlı Erişim
                  </h3>
                  <div className="space-y-1">
                    {(settings.features.bookmarksEnabled || isAdminUser) && (
                      <Link
                        to="/bookmarks"
                        className="flex items-center gap-3 p-3 rounded-xl hover:bg-surface-100 dark:hover:bg-surface-800 transition-all group"
                      >
                        <div className="w-9 h-9 rounded-xl bg-mavi-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Bookmark className="w-4 h-4 text-mavi-500" />
                        </div>
                        <div className="flex-1">
                          <span className="text-sm font-poppins font-medium text-gray-700 dark:text-gray-300 group-hover:text-mavi-600 dark:group-hover:text-mavi-400 transition-colors">
                            Kaydedilenler
                          </span>
                          {bookmarks.length > 0 && (
                            <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-mavi-100 dark:bg-mavi-900/30 text-mavi-600 dark:text-mavi-400 rounded-full font-medium">
                              {bookmarks.length}
                            </span>
                          )}
                        </div>
                      </Link>
                    )}

                    {(settings.features.flowEnabled || isAdminUser) && (
                      <Link
                        to="/flow/mavililer"
                        className="flex items-center gap-3 p-3 rounded-xl hover:bg-surface-100 dark:hover:bg-surface-800 transition-all group"
                      >
                        <div className="w-9 h-9 rounded-xl bg-purple-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                          <FileText className="w-4 h-4 text-purple-500" />
                        </div>
                        <span className="text-sm font-poppins font-medium text-gray-700 dark:text-gray-300 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                          Akış & Makaleler
                        </span>
                      </Link>
                    )}

                    {(settings.features.chatEnabled || isAdminUser) && (
                      <Link
                        to="/rooms"
                        className="flex items-center gap-3 p-3 rounded-xl hover:bg-surface-100 dark:hover:bg-surface-800 transition-all group"
                      >
                        <div className="w-9 h-9 rounded-xl bg-green-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Users className="w-4 h-4 text-green-500" />
                        </div>
                        <span className="text-sm font-poppins font-medium text-gray-700 dark:text-gray-300 group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors">
                          Sohbet Odaları
                        </span>
                      </Link>
                    )}

                    <Link
                      to="/notifications"
                      className="flex items-center gap-3 p-3 rounded-xl hover:bg-surface-100 dark:hover:bg-surface-800 transition-all group"
                    >
                      <div className="w-9 h-9 rounded-xl bg-red-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Bell className="w-4 h-4 text-red-500" />
                      </div>
                      <div className="flex-1">
                        <span className="text-sm font-poppins font-medium text-gray-700 dark:text-gray-300 group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors">
                          Bildirimler
                        </span>
                        {unreadCount > 0 && (
                          <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full font-bold">
                            {unreadCount}
                          </span>
                        )}
                      </div>
                    </Link>

                    <Link
                      to="/inbox"
                      className="flex items-center gap-3 p-3 rounded-xl hover:bg-surface-100 dark:hover:bg-surface-800 transition-all group"
                    >
                      <div className="w-9 h-9 rounded-xl bg-orange-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <MessageSquare className="w-4 h-4 text-orange-500" />
                      </div>
                      <span className="text-sm font-poppins font-medium text-gray-700 dark:text-gray-300 group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors">
                        Mesajlar
                      </span>
                    </Link>

                    {(settings.features.searchEnabled || isAdminUser) && (
                      <Link
                        to="/search"
                        className="flex items-center gap-3 p-3 rounded-xl hover:bg-surface-100 dark:hover:bg-surface-800 transition-all group"
                      >
                        <div className="w-9 h-9 rounded-xl bg-cyan-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Search className="w-4 h-4 text-cyan-500" />
                        </div>
                        <span className="text-sm font-poppins font-medium text-gray-700 dark:text-gray-300 group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors">
                          Gelişmiş Arama
                        </span>
                      </Link>
                    )}
                  </div>
                </div>
              )}

              {(settings.features.zapsEnabled || isAdminUser) && (
                <div className="space-y-3">
                  <h3 className="text-xs font-poppins font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                    BİLGİ & DESTEK
                  </h3>
                  <div className="p-3.5 bg-gradient-to-br from-mavi-50 to-purple-50 dark:from-mavi-900/20 dark:to-purple-900/20 rounded-xl border border-mavi-100 dark:border-mavi-800/30">
                    <div className="flex items-center gap-2 mb-2">
                      <Zap className="w-4 h-4 text-orange-500" />
                      <span className="text-sm font-poppins font-semibold text-gray-800 dark:text-gray-200">
                        Entry Yazarak Para Kazanın
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed font-merriweather italic">
                      Paylaştığınız entry'ler için Bitcoin ile bahşiş toplayabilir ve kazanç elde edebilirsiniz.
                    </p>
                  </div>
                </div>
              )}

              {/* Hakkında */}
              <Link
                to="/about"
                className="flex items-center gap-3 p-3 rounded-xl hover:bg-surface-100 dark:hover:bg-surface-800 transition-all group"
              >
                <div className="w-9 h-9 rounded-xl bg-gray-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Info className="w-4 h-4 text-gray-500" />
                </div>
                <span className="text-sm font-poppins font-medium text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                  Hakkında
                </span>
              </Link>
            </div>
          </aside>
        )}
      </div>

    </div>
  )
}
